import { useState, type KeyboardEvent } from 'react'

import type { KeywordChip } from '@/features/recruiter/api/candidates.api'

interface KeywordChipInputProps {
  chips: KeywordChip[]
  onChange: (chips: KeywordChip[]) => void
  suggestions?: string[]
}

/**
 * Two coordinated boxes — Include and Exclude — matching Naukri Resdex's
 * actual keyword UX rather than one input cycling through modes. An
 * Include chip can be starred to mark it mandatory (must match exactly,
 * not just included); Exclude chips are always exclude-mode. Both write
 * into the same flat `KeywordChip[]` the backend already expects (see
 * CandidateSearchQueryBuilder::keywordClause()) — only the input UX
 * changed, not the API shape.
 */
export function KeywordChipInput({ chips, onChange, suggestions = [] }: KeywordChipInputProps) {
  const [includeDraft, setIncludeDraft] = useState('')
  const [excludeDraft, setExcludeDraft] = useState('')

  const includeChips = chips.filter((c) => c.mode !== 'exclude')
  const excludeChips = chips.filter((c) => c.mode === 'exclude')

  const addChip = (value: string, mode: KeywordChip['mode']) => {
    const trimmed = value.trim()
    if (trimmed && !chips.some((c) => c.value === trimmed)) {
      onChange([...chips, { value: trimmed, mode }])
    }
  }

  const removeChip = (value: string) => onChange(chips.filter((c) => c.value !== value))

  const toggleMandatory = (value: string) => {
    onChange(
      chips.map((c) =>
        c.value === value ? { ...c, mode: c.mode === 'mandatory' ? 'include_and' : 'mandatory' } : c,
      ),
    )
  }

  const onIncludeKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addChip(includeDraft, 'include_and')
      setIncludeDraft('')
    }
  }

  const onExcludeKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addChip(excludeDraft, 'exclude')
      setExcludeDraft('')
    }
  }

  const visibleSuggestions = suggestions.filter((s) => !chips.some((c) => c.value === s)).slice(0, 10)

  return (
    <div>
      <div className="mb-2">
        <span className="form-label fw-medium d-block">Include</span>
        <div className="form-control d-flex flex-wrap align-items-center gap-1" style={{ minHeight: 44 }}>
          {includeChips.map((chip) => (
            <span
              key={chip.value}
              className={`badge d-flex align-items-center gap-1 py-1 px-2 ${
                chip.mode === 'mandatory' ? 'bg-success-transparent' : 'bg-primary-transparent'
              }`}
            >
              <button
                type="button"
                className="btn btn-link p-0 fs-13 lh-1 text-decoration-none"
                onClick={() => toggleMandatory(chip.value)}
                title={chip.mode === 'mandatory' ? 'Mandatory — click to make optional' : 'Click to mark as mandatory'}
                aria-label={chip.mode === 'mandatory' ? `Unmark ${chip.value} as mandatory` : `Mark ${chip.value} as mandatory`}
              >
                {chip.mode === 'mandatory' ? '★' : '☆'}
              </button>
              {chip.value}
              <button
                type="button"
                className="btn-close fs-10"
                aria-label={`Remove ${chip.value}`}
                onClick={() => removeChip(chip.value)}
              />
            </span>
          ))}
          <input
            type="text"
            className="border-0 flex-grow-1"
            style={{ outline: 'none', minWidth: 120 }}
            placeholder={includeChips.length === 0 ? 'e.g. soft skills trainer' : 'Add another…'}
            value={includeDraft}
            onChange={(e) => setIncludeDraft(e.target.value)}
            onKeyDown={onIncludeKeyDown}
            onBlur={() => {
              addChip(includeDraft, 'include_and')
              setIncludeDraft('')
            }}
          />
        </div>
        <p className="fs-11 text-muted mb-0 mt-1">Click ☆ to mark a keyword as mandatory (must match exactly).</p>
        {visibleSuggestions.length > 0 && (
          <div className="mt-2 d-flex flex-wrap gap-1">
            {visibleSuggestions.map((s) => (
              <button
                key={s}
                type="button"
                className="btn btn-sm btn-light border rounded-pill fs-12 py-1 px-2"
                onClick={() => addChip(s, 'include_and')}
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>

      <div>
        <span className="form-label fw-medium d-block">Exclude</span>
        <div className="form-control d-flex flex-wrap align-items-center gap-1" style={{ minHeight: 44 }}>
          {excludeChips.map((chip) => (
            <span key={chip.value} className="badge bg-danger-transparent d-flex align-items-center gap-1 py-1 px-2">
              {chip.value}
              <button
                type="button"
                className="btn-close fs-10"
                aria-label={`Remove ${chip.value}`}
                onClick={() => removeChip(chip.value)}
              />
            </span>
          ))}
          <input
            type="text"
            className="border-0 flex-grow-1"
            style={{ outline: 'none', minWidth: 120 }}
            placeholder={excludeChips.length === 0 ? 'Keywords to exclude' : 'Add another…'}
            value={excludeDraft}
            onChange={(e) => setExcludeDraft(e.target.value)}
            onKeyDown={onExcludeKeyDown}
            onBlur={() => {
              addChip(excludeDraft, 'exclude')
              setExcludeDraft('')
            }}
          />
        </div>
      </div>
    </div>
  )
}
